import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { chavesDeContratos, opcoesDaFonte } from '../api/contratos';
import { SeloDeEstadoDaFonte } from '../componentes/SeloDeEstadoDaFonte';
import { comoErroDaApi, ErroDaApi } from '../../../shared/api/erros';
import { EstadoDeCarregamento } from '../../../shared/ui/EstadoDeCarregamento';
import { EstadoDeErro } from '../../../shared/ui/EstadoDeErro';
import { Identificador } from '../../../shared/ui/Identificador';
import {
  aprovarItem,
  aprovarItensEmLote,
  aprovarLote,
  aprovarRegra,
  ajustarRegra,
  ativarFonte,
  chavesDeRevisao,
  opcoesDaFila,
  rejeitarRegra,
  reprocessarFonte,
} from './api/revisao';
import { DiffDaFonte } from './DiffDaFonte';
import { FilaDeRevisao } from './FilaDeRevisao';
import { PassosDaRevisao } from './PassosDaRevisao';
import { ReferenciaDaFonte } from './ReferenciaDaFonte';
import { RevisaoDaRegra } from './RevisaoDaRegra';
import { ETAPAS_DE_REPROCESSO, ROTULOS_DE_ETAPA, type EtapaDeReprocesso, type RegraDaFila, type TipoDeRegra } from './api/tipos';

/**
 * Botão de ativar a fonte (`TP-050`, T9/D12).
 *
 * Desabilitado PROATIVAMENTE quando a fila (`GET /revisao/regras`) ainda
 * tem regra `extraida` para esta fonte — a MESMA consulta que
 * `FilaDeRevisao` usa, cacheada pelo react-query (mesma `queryKey`): não
 * duplica chamada. A API não expõe `itens_pendentes` fora do `409` de
 * `POST /fontes/{id}/ativar`, então esse segundo motivo só aparece depois
 * de uma tentativa (a mensagem do `ErrorCatalog` já traz "Faltam N
 * regra(s) e M item(ns)...").
 */
function BotaoDeAtivar({ fonteId }: { fonteId: string }): React.JSX.Element {
  const cliente = useQueryClient();
  const consultaDaFila = useQuery(opcoesDaFila(fonteId));
  const regrasPendentes = consultaDaFila.data?.length ?? 0;
  const [emCurso, definirEmCurso] = useState(false);
  const [motivoPendente, definirMotivoPendente] = useState<string | null>(null);
  const [ativada, definirAtivada] = useState(false);
  const [aprovandoItens, definirAprovandoItens] = useState(false);
  const [itensAprovados, definirItensAprovados] = useState<number | null>(null);
  const [erroDosItens, definirErroDosItens] = useState<string | null>(null);

  return (
    <Stack spacing={0.5}>
      {/* Contrato com tabela de preço traz centenas de itens com unidade incerta: um a um, a revisão não fecha. */}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button
          variant="outlined"
          disabled={aprovandoItens || ativada}
          onClick={() => {
            definirAprovandoItens(true);
            definirErroDosItens(null);
            aprovarItensEmLote(fonteId)
              .then((aprovados) => {
                definirItensAprovados(aprovados);
                definirMotivoPendente(null);
                void cliente.invalidateQueries({ queryKey: chavesDeRevisao.fila(fonteId) });
                void cliente.invalidateQueries({ queryKey: chavesDeRevisao.diff(fonteId) });
                void cliente.invalidateQueries({ queryKey: chavesDeContratos.fonte(fonteId) });
              })
              .catch((causa: unknown) => {
                definirErroDosItens(comoErroDaApi(causa).message);
              })
              .finally(() => {
                definirAprovandoItens(false);
              });
          }}
        >
          Aprovar todos os itens pendentes
        </Button>
        {itensAprovados !== null ? (
          <Typography variant="caption" color="success.main">
            {itensAprovados === 0 ? 'Nenhum item pendente.' : `${String(itensAprovados)} item(ns) aprovado(s).`}
          </Typography>
        ) : null}
        {erroDosItens !== null ? (
          <Typography variant="caption" color="error.main" role="alert">
            {erroDosItens}
          </Typography>
        ) : null}
      </Stack>
      {regrasPendentes > 0 ? (
        <Alert severity="warning" role="alert">
          Faltam {regrasPendentes} regra(s) para decidir antes de ativar esta fonte.
        </Alert>
      ) : null}
      <Button
        variant="contained"
        color="success"
        disabled={emCurso || ativada || regrasPendentes > 0}
        onClick={() => {
          definirEmCurso(true);
          definirMotivoPendente(null);
          ativarFonte(fonteId)
            .then(() => {
              definirAtivada(true);
              void cliente.invalidateQueries({ queryKey: chavesDeContratos.fonte(fonteId) });
            })
            .catch((causa: unknown) => {
              definirMotivoPendente(comoErroDaApi(causa).message);
            })
            .finally(() => {
              definirEmCurso(false);
            });
        }}
      >
        {ativada ? 'Fonte ativada' : 'Ativar fonte'}
      </Button>
      {motivoPendente !== null ? (
        <Alert severity="warning" role="alert">
          {motivoPendente}
        </Alert>
      ) : null}
    </Stack>
  );
}

/** Reprocesso por etapa (`TP-050`, T11): devolve a fonte ao estado de entrada da etapa, sem apagar o que já foi revisado. */
function ReprocessarFonte({ fonteId }: { fonteId: string }): React.JSX.Element {
  const cliente = useQueryClient();
  const [etapa, definirEtapa] = useState<EtapaDeReprocesso>('regras');
  const [emCurso, definirEmCurso] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);
  const [sucesso, definirSucesso] = useState<string | null>(null);

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <TextField
        select
        label="Reprocessar etapa"
        size="small"
        value={etapa}
        onChange={(evento) => {
          definirEtapa(evento.target.value as EtapaDeReprocesso);
        }}
        sx={{ minWidth: 220 }}
      >
        {ETAPAS_DE_REPROCESSO.map((valor) => (
          <MenuItem key={valor} value={valor}>
            {ROTULOS_DE_ETAPA[valor]}
          </MenuItem>
        ))}
      </TextField>
      <Button
        variant="outlined"
        disabled={emCurso}
        onClick={() => {
          definirEmCurso(true);
          definirErro(null);
          definirSucesso(null);
          reprocessarFonte(fonteId, etapa)
            .then((estado) => {
              definirSucesso(`Fonte devolvida ao estado de entrada da etapa (${estado}).`);
              void cliente.invalidateQueries({ queryKey: chavesDeContratos.fonte(fonteId) });
              void cliente.invalidateQueries({ queryKey: chavesDeRevisao.fila(fonteId) });
            })
            .catch((causa: unknown) => {
              definirErro(comoErroDaApi(causa).message);
            })
            .finally(() => {
              definirEmCurso(false);
            });
        }}
      >
        Reprocessar
      </Button>
      {sucesso !== null ? (
        <Typography variant="caption" color="success.main">
          {sucesso}
        </Typography>
      ) : null}
      {erro !== null ? (
        <Typography variant="caption" color="error.main" role="alert">
          {erro}
        </Typography>
      ) : null}
    </Stack>
  );
}

/**
 * A tela de revisão de uma fonte (`TP-050`, T9/T10/T11): a fila de regras
 * extraídas ao lado do detalhe (trecho destacado na página), o diff da
 * fonte, e o botão de ativar — sem sair da tela.
 */
export function PaginaDeRevisao(): React.JSX.Element {
  const { fonteId } = useParams<{ fonteId: string }>();
  const navegar = useNavigate();
  const cliente = useQueryClient();
  const [regraSelecionada, definirRegraSelecionada] = useState<RegraDaFila | null>(null);

  const consultaDaFonte = useQuery({ ...opcoesDaFonte(fonteId ?? ''), enabled: fonteId !== undefined });

  const invalidarFila = (): void => {
    if (fonteId === undefined) return;
    void cliente.invalidateQueries({ queryKey: chavesDeRevisao.fila(fonteId) });
    void cliente.invalidateQueries({ queryKey: chavesDeRevisao.diff(fonteId) });
    void cliente.invalidateQueries({ queryKey: chavesDeContratos.fonte(fonteId) });
  };

  if (fonteId === undefined) {
    return (
      <Alert severity="error" role="alert">
        Fonte não informada na rota.
      </Alert>
    );
  }

  if (consultaDaFonte.isPending) return <EstadoDeCarregamento linhas={6} />;

  if (consultaDaFonte.isError) {
    if (consultaDaFonte.error instanceof ErroDaApi && consultaDaFonte.error.status === 401) {
      void navegar('/entrar', { replace: true });
    }
    return (
      <EstadoDeErro
        erro={consultaDaFonte.error}
        contexto="Não foi possível carregar a fonte"
        aoReprocessar={() => {
          void consultaDaFonte.refetch();
        }}
      />
    );
  }

  const fonte = consultaDaFonte.data;

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h1">Revisão da fonte</Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
          <ReferenciaDaFonte
            fonte={{
              tipo: fonte.tipo,
              criadaEm: fonte.criadaEm,
              paginas: fonte.paginas,
              totalDeItens: fonte.totalDeItens,
            }}
          />
          <SeloDeEstadoDaFonte estado={fonte.estado} />
          <Identificador valor={fonte.id} />
        </Stack>
      </Box>

      <PassosDaRevisao estado={fonte.estado} />

      {fonte.estado === 'rejeitada' && fonte.motivoDaRecusa !== null ? (
        <Alert severity="error" role="alert">
          A fonte foi rejeitada: {fonte.motivoDaRecusa}
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <ReprocessarFonte fonteId={fonteId} />
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 5 }}>
            <FilaDeRevisao
              fonteId={fonteId}
              estadoDaFonte={fonte.estado}
              regraSelecionadaId={regraSelecionada?.id ?? null}
              aoSelecionar={(regra) => {
                definirRegraSelecionada(regra);
              }}
              aoAprovarLote={(tipo: TipoDeRegra) =>
                aprovarLote(fonteId, tipo).then((aprovadas) => {
                  invalidarFila();
                  if (regraSelecionada?.tipo === tipo) definirRegraSelecionada(null);
                  return aprovadas;
                })
              }
            />
          </Grid>
          <Grid size={{ xs: 12, md: 7 }}>
            {regraSelecionada === null ? (
              <Typography variant="body2" color="text.secondary">
                Escolha uma regra na fila para ver o trecho e decidir.
              </Typography>
            ) : (
              <RevisaoDaRegra
                key={regraSelecionada.id}
                regra={regraSelecionada}
                aoAprovar={() =>
                  aprovarRegra(regraSelecionada.id).then((resultado) => {
                    invalidarFila();
                    definirRegraSelecionada(null);
                    return resultado;
                  })
                }
                aoRejeitar={(motivo) =>
                  rejeitarRegra(regraSelecionada.id, motivo).then((resultado) => {
                    invalidarFila();
                    definirRegraSelecionada(null);
                    return resultado;
                  })
                }
                aoAjustar={(escopo, efeito) =>
                  ajustarRegra(regraSelecionada.id, escopo, efeito).then((resultado) => {
                    invalidarFila();
                    definirRegraSelecionada(null);
                    return resultado;
                  })
                }
              />
            )}
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <DiffDaFonte
          fonteId={fonteId}
          aoAprovarItem={(itemId) =>
            aprovarItem(itemId).then(() => {
              void cliente.invalidateQueries({ queryKey: chavesDeContratos.fonte(fonteId) });
            })
          }
        />
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1}>
          <Typography variant="h3">Ativar</Typography>
          <Typography variant="body2" color="text.secondary">
            Só ativa sem regra pendente e com os itens aprovados. Itens com unidade de cobrança incerta podem ser aprovados
            um a um no quadro acima ou todos de uma vez aqui.
          </Typography>
          <BotaoDeAtivar fonteId={fonteId} />
        </Stack>
      </Paper>
    </Stack>
  );
}
